import { getTasksFromGantt } from '../resources/tasks/getTasksFromGantt';

const TASK_PREFIX = 'task_';
const LINK_PREFIX = 'link_';

/**
 * Maps task IDs to their respective indices in the gantt and finds the maximum UID.
 * Uses early return pattern to handle different validation strategies.
 *
 * @param {Array} tasks - Array of tasks from the gantt
 * @param {boolean} isValidateUIDs - Flag to determine the UID validation strategy
 *  - If true: Uses unique_correlative_id property without offset
 *  - If false: Uses correlative_id property with offset of 1
 * @returns {Object} Object containing:
 *  - taskMap: Mapping of task IDs to their UIDs
 *  - maxUid: Maximum UID value found (or tasks.length when isValidateUIDs is false)
 */
const mapTaskIds = (tasks, isValidateUIDs) => {
  if (!tasks || !tasks.length) {
    return { taskMap: {}, maxUid: 0 };
  }

  if (isValidateUIDs) {
    let maxUid = 0;
    const taskMap = tasks.reduce((map, task) => {
      const uid = Number(task.unique_correlative_id);
      map[`${TASK_PREFIX}${task.id}`] = uid;
      maxUid = Math.max(maxUid, uid);
      return map;
    }, {});

    return { taskMap, maxUid };
  }

  const taskMap = tasks.reduce((map, task) => {
    const uid = Number(task.correlative_id) + 1;
    map[`${TASK_PREFIX}${task.id}`] = uid;
    return map;
  }, {});

  return { taskMap, maxUid: tasks.length };
};

/**
 * Maps link IDs to their respective indices in the gantt
 *
 * @param {Array} links - Array of links from the gantt
 * @param {number} linkOffset - Base offset for link indices
 * @param {number} taskOffset - Additional offset for task indices
 * @returns {Object} Mapping of link IDs to their indices
 */
const mapLinkIds = (links, linkOffset, taskOffset) => {
  const linkMap = {};

  links.forEach(({ id }, index) => {
    linkMap[`${LINK_PREFIX}${id}`] = linkOffset + index + taskOffset;
  });

  return linkMap;
};

/**
 * Creates a mapping of task and link IDs to their respective indices in the gantt.
 *
 * @param {Object} gantt - The gantt object.
 * @returns {Object} An object mapping task and link IDs to their corresponding indices.
 * The keys are in the format `task_{id}` or `link_{id}` and the values are the indices.
 */
export const createGanttElementIdMapping = (gantt, isValidateUIDs) => {
  const tasks = getTasksFromGantt(gantt);
  const links = gantt.getLinks();

  const { taskMap, maxUid } = mapTaskIds(tasks, isValidateUIDs);
  const linkOffset = maxUid + 1;
  const linkIdMap = mapLinkIds(links, linkOffset, 0);
  const idMap = { ...taskMap, ...linkIdMap };
  return idMap;
};
